import * as React from "react"

interface Anchor {
  /** The anchored point, as a fraction of the content's size. */
  fx: number
  fy: number
  /** Where that point sat in the viewport, in CSS pixels from its top left. */
  px: number
  py: number
}

function contentOf(
  view: HTMLElement,
  contentRef: React.RefObject<HTMLElement | null> | undefined,
): HTMLElement | null {
  const content = contentRef?.current ?? view.firstElementChild
  return content instanceof HTMLElement ? content : null
}

/**
 * Keep one point of the document still through a zoom, for a viewer that
 * scrolls for real and lays its pages out at the zoomed size: the pointer for
 * a wheel zoom, the middle of the view otherwise.
 *
 * Call the returned function just before changing the zoom. The scroll is
 * corrected once the new layout lands — `layoutKey` changing marks that,
 * typically the rendered page width — rather than at the moment of the input.
 * Measured against the content's box rather than the scroll size, so gutters
 * and a page centred while it fits do not skew the anchor.
 */
export function useScrollZoomAnchor(
  viewportRef: React.RefObject<HTMLElement | null>,
  layoutKey: number,
  contentRef?: React.RefObject<HTMLElement | null>,
): (at?: { x: number; y: number }) => void {
  const pending = React.useRef<Anchor | null>(null)

  React.useLayoutEffect(() => {
    const view = viewportRef.current
    const anchor = pending.current
    pending.current = null
    if (!view || !anchor) return
    const content = contentOf(view, contentRef)
    if (!content) return
    const viewRect = view.getBoundingClientRect()
    const rect = content.getBoundingClientRect()
    view.scrollLeft += rect.left + anchor.fx * rect.width - (viewRect.left + anchor.px)
    view.scrollTop += rect.top + anchor.fy * rect.height - (viewRect.top + anchor.py)
  }, [viewportRef, contentRef, layoutKey])

  return React.useCallback(
    (at) => {
      const view = viewportRef.current
      const content = view ? contentOf(view, contentRef) : null
      if (!view || !content) return
      const viewRect = view.getBoundingClientRect()
      const rect = content.getBoundingClientRect()
      const x = at?.x ?? viewRect.left + view.clientWidth / 2
      const y = at?.y ?? viewRect.top + view.clientHeight / 2
      pending.current = {
        fx: (x - rect.left) / (rect.width || 1),
        fy: (y - rect.top) / (rect.height || 1),
        px: x - viewRect.left,
        py: y - viewRect.top,
      }
    },
    [viewportRef, contentRef],
  )
}
